import { reportWebsiteError } from './core/monitoring/websiteErrors';
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { Provider } from 'react-redux';
import { store } from './core/store/store';
import { BrowserRouter } from 'react-router-dom';
import { FirebaseOptions, initializeApp } from 'firebase/app';
import { deleteToken, getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import { getAuth } from 'firebase/auth';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const firebaseConfig: FirebaseOptions = {
  apiKey: process.env.REACT_APP_GOOGLE_API_KEY,
  authDomain: process.env.REACT_APP_GOOGLE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
};

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
const vapidKey = process.env.REACT_APP_VAPID_KEY;
let messagingServiceWorkerRegistrationPromise: Promise<ServiceWorkerRegistration | undefined> | null = null;
let hasForegroundMessageListener = false;

if ('serviceWorker' in navigator) {
  messagingServiceWorkerRegistrationPromise = navigator.serviceWorker.register('/firebase-messaging-sw.js')
    .then((registration) => {
      console.log('SW registrado con éxito:', registration);
      return registration;
    }).catch((err) => {
      console.error('Error al registrar SW:', err);
      return undefined;
    });
}

const getMessagingServiceWorkerRegistration = async () => {
  if (!messagingServiceWorkerRegistrationPromise) {
    return undefined;
  }

  return messagingServiceWorkerRegistrationPromise;
};

export { getMessagingServiceWorkerRegistration };

const buildNotificationDetails = (payload: any) => {
  const title = payload?.notification?.title || "Notification";
  const body = payload?.notification?.body || "";
  const icon = payload?.data?.profile_pictures?.split(',')[0] || "/favicon.ico";
  const type = payload?.data?.type || "admin";
  const id =
    payload?.data?.match_id ||
    payload?.data?.chat_id ||
    payload?.data?.notification_id ||
    "";

  let url = "";
  if (type === "message") {
    url = `/messages/${id}`;
  } else if (type === "partner") {
    url = "/settings";
  } else if (type === "new_nearby_members") {
    url = "/";
  }

  return {
    title,
    options: {
      body,
      icon,
      data: { url, type, id },
    },
  };
};

const showForegroundNotification = async (payload: any) => {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") {
    return;
  }

  const { title, options } = buildNotificationDetails(payload);
  const serviceWorkerRegistration = await getMessagingServiceWorkerRegistration();

  if (serviceWorkerRegistration) {
    await serviceWorkerRegistration.showNotification(title, options);
    return;
  }

  new Notification(title, options);
};

const notifyNewNearbyMembers = (payload: any) => {
  if (payload?.data?.type !== "new_nearby_members") {
    return;
  }

  window.dispatchEvent(new CustomEvent("unjabbed:new-nearby-members"));
};

const ensureForegroundMessageListener = async () => {
  if (hasForegroundMessageListener) {
    return;
  }

  const supported = await isSupported();
  if (!supported) {
    return;
  }

  const messaging = getMessaging(app);
  onMessage(messaging, (payload) => {
    notifyNewNearbyMembers(payload);
    void showForegroundNotification(payload);
  });

  hasForegroundMessageListener = true;
};

export const getNotificationToken = async ({
  requestPermission = true,
  forceRefresh = false,
}: {
  requestPermission?: boolean;
  forceRefresh?: boolean;
} = {}) => {
  const supported = await isSupported();
  if (!supported) {
    console.warn("Este navegador no soporta notificaciones push.");
    return null;
  }

  if (typeof Notification === "undefined") {
    return null;
  }

  const permission = requestPermission
    ? await Notification.requestPermission()
    : Notification.permission;

  if (permission !== 'granted') {
    console.log("Permiso de notificaciones denegado.");
    return null;
  }

  try {
    const messaging = getMessaging(app);
    await ensureForegroundMessageListener();

    if (forceRefresh) {
      try {
        await deleteToken(messaging);
      } catch (error) {
        console.warn("No se pudo limpiar el token push anterior:", error);
      }
    }

    const serviceWorkerRegistration = await getMessagingServiceWorkerRegistration();
    const token = await getToken(messaging, {
      vapidKey,
      ...(serviceWorkerRegistration ? { serviceWorkerRegistration } : {}),
    });
    return token || null;
  } catch (error) {
    console.error("Error al obtener el token FCM:", error);
    return null;
  }
};

export const handleEnableNotifications = async () => getNotificationToken();

const recordBootstrapError = (storageKey: string, value: unknown) => {
  if (typeof window === "undefined") {
    return;
  }

  const message =
    typeof value === "string"
      ? value
      : value instanceof Error
        ? value.message
        : String(value ?? "unknown_error");

  window.localStorage.setItem(storageKey, message);
};

window.addEventListener("error", (event) => {
  recordBootstrapError("APP_BOOT_ERROR", event.error ?? event.message);
});

window.addEventListener("unhandledrejection", (event) => {
  recordBootstrapError("APP_BOOT_REJECTION", event.reason);
});

class RootRenderBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    reportWebsiteError(error, "ROOT_RENDER_ERROR");
    recordBootstrapError("ROOT_RENDER_ERROR", error);
  }

  render() {
    if (this.state.hasError) {
      return null;
    }

    return this.props.children;
  }
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <Provider store={store}>
    <BrowserRouter>
      <RootRenderBoundary>
        <App />
        <ToastContainer />
      </RootRenderBoundary>
    </BrowserRouter>
  </Provider>
);

reportWebVitals();
